import { FavoriteId } from '@/shared/enums/favorite-id.enum';
import { ResourcesSectionLabel } from '@enums';
import { ChildPageRoute, GknRoutePath } from 'src/app/app-routing.enum';
import { FavoriteDefinition } from '../../types/favorite';

/**
 * Central registry mapping favorite items to their navigation metadata.
 *
 * This map serves as the single source of truth for all favoritable content
 * in Genesys Orchestrators Portal, enabling users to bookmark and quickly
 * access key resources, tools, and sections across the platform.
 *
 * Each entry provides the display label and routing information needed to
 * navigate directly to that content when selected from a user's favorites list.
 *
 * @example
 * export class MyComponent {
 *   favorite = FavoriteId.MY_FAVORITE;
 * }
 *
 * <a [routerLink]="'/' + favorite.routerLink"
 * // Get navigation info for a user's favorited item
 * this.favorite = FAVORITE_DEFINITION_MAP[FavoriteId.DEVELOPER_CENTER];
 */
export const FAVORITE_DEFINITION_MAP: { [key in FavoriteId]: FavoriteDefinition } = {
    [FavoriteId.ADVOCACY]: {
        favoriteId: FavoriteId.ADVOCACY,
        label: 'Advocacy',
        routerLink: GknRoutePath.ADVOCACY,
    },
    [FavoriteId.ACHIEVERS_LOUNGE]: {
        favoriteId: FavoriteId.ACHIEVERS_LOUNGE,
        label: "Achiever's Lounge",
        routerLink: `${GknRoutePath.PROFILE}/${ChildPageRoute.ACHIEVERS_LOUNGE}`,
    },
    [FavoriteId.APPFOUNDRY]: {
        favoriteId: FavoriteId.APPFOUNDRY,
        label: 'AppFoundry',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.APPFOUNDRY}`,
    },
    [FavoriteId.BOOST_YOUR_KNOWLEDGE_WEBINARS]: {
        favoriteId: FavoriteId.BOOST_YOUR_KNOWLEDGE_WEBINARS,
        label: 'Boost Your Knowledge Webinars',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.ONBOARDING}`,
        fragment: ChildPageRoute.BOOST_YOUR_KNOWLEDGE_WEBINARS,
    },
    [FavoriteId.BOOST_YOUR_KNOWLEDGE_WEBINARS_LIBRARY]: {
        favoriteId: FavoriteId.BOOST_YOUR_KNOWLEDGE_WEBINARS_LIBRARY,
        label: 'Boost Your Knowledge Webinars Library',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.ONBOARDING}/${ChildPageRoute.BOOST_YOUR_KNOWLEDGE_WEBINARS_LIBRARY}`,
    },
    [FavoriteId.CERTIFICATIONS]: {
        favoriteId: FavoriteId.CERTIFICATIONS,
        label: 'Certifications',
        routerLink: GknRoutePath.EDUCATION,
        fragment: 'certifications',
    },
    [FavoriteId.CHECKOUT_ACTIVITIES]: {
        favoriteId: FavoriteId.CHECKOUT_ACTIVITIES,
        label: 'Community Activities',
        routerLink: GknRoutePath.COMMUNITY,
        fragment: 'activities',
    },
    [FavoriteId.COMMUNITY_LATEST_DISCUSSIONS]: {
        favoriteId: FavoriteId.COMMUNITY_LATEST_DISCUSSIONS,
        label: 'Genesys Community Latest Discussions',
        routerLink: GknRoutePath.COMMUNITY,
        fragment: 'latest-discussions',
    },
    [FavoriteId.GENESYS_COMMUNITY_YOUTUBE]: {
        favoriteId: FavoriteId.GENESYS_COMMUNITY_YOUTUBE,
        label: 'Community YouTube',
        routerLink: GknRoutePath.COMMUNITY,
        fragment: 'youtube',
    },
    [FavoriteId.CX_EVOLUTION]: {
        favoriteId: FavoriteId.CX_EVOLUTION,
        label: 'CX Evolution',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.CX_EVOLUTION}`,
    },
    [FavoriteId.DEVELOPER_CENTER]: {
        favoriteId: FavoriteId.DEVELOPER_CENTER,
        label: 'Developer Center',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.PRODUCT_KNOWLEDGE}`,
        fragment: 'developer-center',
    },
    [FavoriteId.EDUCATION_ENROLLMENTS]: {
        favoriteId: FavoriteId.EDUCATION_ENROLLMENTS,
        label: 'Enrollments',
        routerLink: GknRoutePath.EDUCATION,
        fragment: 'enrollments',
    },
    [FavoriteId.EDUCATION_FEATURED_VIDEOS]: {
        favoriteId: FavoriteId.EDUCATION_FEATURED_VIDEOS,
        label: 'Featured education videos',
        routerLink: GknRoutePath.EDUCATION,
        fragment: 'featured',
    },
    [FavoriteId.RECOMMENDATIONS]: {
        favoriteId: FavoriteId.RECOMMENDATIONS,
        label: 'Featured education videos',
        routerLink: GknRoutePath.EDUCATION,
        fragment: 'featured',
    },
    [FavoriteId.EDUCATION_WEBINARS]: {
        favoriteId: FavoriteId.EDUCATION_WEBINARS,
        label: 'Education Webinars',
        routerLink: GknRoutePath.EDUCATION,
        fragment: 'webinars',
    },
    [FavoriteId.FUNDAMENTALS]: {
        favoriteId: FavoriteId.FUNDAMENTALS,
        label: 'Fundamentals',
        routerLink: GknRoutePath.HOME,
        fragment: 'fundamentals',
    },
    [FavoriteId.GENESYS_BLOG]: {
        favoriteId: FavoriteId.GENESYS_BLOG,
        label: 'Genesys Blog',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.GENESYS_NEWS_AND_EVENTS}`,
        fragment: 'blog',
    },
    [FavoriteId.GENESYS_EVENTS]: {
        favoriteId: FavoriteId.GENESYS_EVENTS,
        label: 'Genesys Events',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.GENESYS_NEWS_AND_EVENTS}`,
        fragment: 'events',
    },
    [FavoriteId.GENESYS_NEWS]: {
        favoriteId: FavoriteId.GENESYS_NEWS,
        label: 'Genesys News',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.GENESYS_NEWS_AND_EVENTS}`,
        fragment: 'news',
    },
    [FavoriteId.GENESYS_SOCIAL]: {
        favoriteId: FavoriteId.GENESYS_SOCIAL,
        label: 'Genesys Social',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.GENESYS_NEWS_AND_EVENTS}`,
        fragment: 'social',
    },
    [FavoriteId.HELPFUL_LINKS]: {
        favoriteId: FavoriteId.HELPFUL_LINKS,
        label: 'Support Helpful Links',
        routerLink: GknRoutePath.SUPPORT,
        fragment: 'helpful-links',
    },
    [FavoriteId.PRODUCT_ANNOUNCEMENTS]: {
        favoriteId: FavoriteId.PRODUCT_ANNOUNCEMENTS,
        label: 'Product Announcements',
        routerLink: GknRoutePath.SUPPORT,
        fragment: 'announcements',
    },
    [FavoriteId.PRODUCT_HELP]: {
        favoriteId: FavoriteId.PRODUCT_HELP,
        label: 'Product Help',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.PRODUCT_KNOWLEDGE}`,
        fragment: 'help',
    },
    [FavoriteId.PERSONALIZED_VIDEO]: {
        favoriteId: FavoriteId.PERSONALIZED_VIDEO,
        label: '',
        routerLink: GknRoutePath.PROFILE,
    },
    [FavoriteId.ROADMAP_ARCHIVES]: {
        favoriteId: FavoriteId.ROADMAP_ARCHIVES,
        label: 'Roadmap Archives',
        routerLink: `${GknRoutePath.HOME}/${ChildPageRoute.ROADMAP_ARCHIVES}`,
    },
    [FavoriteId.SHORT_TRACKS]: {
        favoriteId: FavoriteId.SHORT_TRACKS,
        label: 'Short Tracks',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.ONBOARDING}`,
        fragment: `${ChildPageRoute.SHORT_TRACKS}`,
    },
    [FavoriteId.STARTER_KITS]: {
        favoriteId: FavoriteId.STARTER_KITS,
        label: 'Starter Kits',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.ONBOARDING}`,
        fragment: ChildPageRoute.STARTER_KITS,
    },
    [FavoriteId.SUPPORT_CASES]: {
        favoriteId: FavoriteId.SUPPORT_CASES,
        label: 'Support Cases',
        routerLink: GknRoutePath.SUPPORT,
        fragment: 'cases',
    },
    [FavoriteId.TIPS_AND_TOOLS]: {
        favoriteId: FavoriteId.TIPS_AND_TOOLS,
        label: 'Tips and Tools',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.PRODUCT_KNOWLEDGE}`,
        fragment: 'tips-and-tools',
    },
    [FavoriteId.STARTER_KITS_GC]: {
        favoriteId: FavoriteId.STARTER_KITS_GC,
        label: ResourcesSectionLabel.STARTER_KITS_GC,
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.ONBOARDING}/${ChildPageRoute.STARTER_KITS}/${ChildPageRoute.STARTER_KITS_GC}`,
    },
    [FavoriteId.STARTER_KITS_WEM]: {
        favoriteId: FavoriteId.STARTER_KITS_WEM,
        label: ResourcesSectionLabel.STARTER_KITS_WEM,
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.ONBOARDING}/${ChildPageRoute.STARTER_KITS}/${ChildPageRoute.STARTER_KITS_WEM}`,
    },
    [FavoriteId.CAPABILITY_ACCELERATOR]: {
        favoriteId: FavoriteId.CAPABILITY_ACCELERATOR,
        label: 'Capability Accelerator',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.CAPABILITY_ACCELERATOR}`,
    },
    [FavoriteId.CAPABILITY_ACCELERATOR_WEB_MESSAGING]: {
        favoriteId: FavoriteId.CAPABILITY_ACCELERATOR_WEB_MESSAGING,
        label: 'Web Messaging Capability Accelerator',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.CAPABILITY_ACCELERATOR}/web-messaging`,
    },
    [FavoriteId.CAPABILITY_ACCELERATOR_PREDICTIVE_ROUTING]: {
        favoriteId: FavoriteId.CAPABILITY_ACCELERATOR_PREDICTIVE_ROUTING,
        label: 'Genesys Predictive Routing Capability Accelerator',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.CAPABILITY_ACCELERATOR}/predictive-routing`,
    },
    [FavoriteId.CAPABILITY_ACCELERATOR_AGENT_COPILOT]: {
        favoriteId: FavoriteId.CAPABILITY_ACCELERATOR_AGENT_COPILOT,
        label: 'Agent Copilot Capability Accelerator',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.CAPABILITY_ACCELERATOR}/agent-copilot`,
    },
    [FavoriteId.CAPABILITY_ACCELERATOR_STA]: {
        favoriteId: FavoriteId.CAPABILITY_ACCELERATOR_STA,
        label: 'Genesys Speech and Text Analytics Capability Accelerator',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.CAPABILITY_ACCELERATOR}/speech-and-text-analytics`,
    },
    [FavoriteId.APP_FOUNDRY]: {
        favoriteId: FavoriteId.APPFOUNDRY,
        label: 'AppFoundry',
        routerLink: `${GknRoutePath.RESOURCES}/${ChildPageRoute.APPFOUNDRY}`,
    },
    [FavoriteId.ARCHIVES]: {
        favoriteId: FavoriteId.ROADMAP_ARCHIVES,
        label: 'Roadmap Archives',
        routerLink: `${GknRoutePath.HOME}/${ChildPageRoute.ROADMAP_ARCHIVES}`,
    },
    [FavoriteId.BEYOND]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.COMMUNITY_QA_SHOW]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.CUSTOMER_SUCCESS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.EXPERT_APPS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.GET_STARTED]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.MARKETING_EVENTS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.LATEST_DISCUSSIONS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.PRODUCT_FUNDAMENTALS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.RELATED_COMMUNITIES]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.PRODUCT_SUPPORT_CENTER]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.EDU_EVENTS_AND_WEBINARS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.MY_CONTACTS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.CONNECT_WITH_GENESYS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.NEWS_FROM_GENESYS_ALL]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.NEWS_FROM_GENESYS_ANNOUNCEMENTS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.NEWS_FROM_GENESYS_BLOG]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.NEWS_FROM_GENESYS_SOCIAL]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.GET_STARTED_MY_PERSONALIZED_VIDEO]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.GET_STARTED_STARTER_KIT]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.GET_STARTED_ONBOARDING]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.GET_STARTED_SHORT_TRACKS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.GET_STARTED_USEFUL_LINKS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.GET_STARTED_TIPS_AND_TOOLS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.CERTIFICATIONS_MY_AWARDS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.CERTIFICATIONS_GET_CERTIFIED]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.CX_EVOLUTION_GET_STARTED]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.CX_EVOLUTION_CONNECT]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.CX_EVOLUTION_LEARN_FROM_OTHERS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.PRODUCT_HELP_ARTICLES]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.PRODUCT_HELP_VIDEOS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.PRODUCT_HELP_USE_CASES]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.PRODUCT_HELP_DOCUMENTATION]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.DEVELOPER_CENTER_TOOLS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.DEVELOPER_CENTER_POPULAR_TOPICS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.DEVELOPER_CENTER_BLOG]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.OWTGI_WRITE_A_REVIEW]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.OWTGI_CUSTOMER_MONTHLY_NEWSLETTER]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.OWTGI_SUPER_HUMAN_SERVICE]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.OWTGI_PRODUCT_IDEAS_LAB]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.OWTGI_GENESYS_USER_GROUPS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.OWTGI_COLLABORATE_WITH_OUR_UX_TEAM]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.OWTGI_DEVELOPER_NEWSLETTER]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.OWTGI_ADVOCACY_PAYS]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.JUST_FOR_ME]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.ADDITIONAL_SITES]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.MY_FAVORITES]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
    [FavoriteId.DEVELOPER_CENTER_POPULAR]: {
        favoriteId: undefined,
        label: '',
        routerLink: '',
        fragment: '',
    },
};
